import type {
  AuthorizationOutcome,
  AuthorizationRequiredStreamEvent,
  HandleMessageStreamEvent,
  InputRequest,
  InputResponse,
} from "eve/client";

/**
 * Projection of an Amy (eve) event log into what the chat renders.
 *
 * Why not eve's `defaultMessageReducer`: it keys every assistant message by
 * `turnId`, and on Amy's real stream that key is unreliable —
 * - turns resumed by an answer carry `turnId: ""` and never emit
 *   `turn.started`;
 * - a durable subagent's questions are PROXIED onto Amy's stream carrying the
 *   CHILD session's turn ids, which collide with old parent turns;
 * - turn ids restart at `turn_0` when a conversation rolls over to a new
 *   eve session;
 * - `subagent.*` events are ignored entirely, so a subagent's questions read
 *   as Amy's own.
 * Stream ORDER is the one reliable signal, so this projection is flat: every
 * user message opens a new user item, and everything after it lands in the
 * assistant item that follows. Subagent work nests under its delegation, and
 * an `input.requested` that arrives while a delegation is open is attributed
 * to that subagent.
 */

/** A client-authored entry interleaved into the persisted log: the answers
 * the user sent (they ride the send request, never the stream). Not counted
 * by the eve stream cursor. */
export interface ClientAnswerEvent {
  type: "client.input.responded";
  data: { responses: InputResponse[]; at: string };
}

export type ThreadLogEvent = HandleMessageStreamEvent | ClientAnswerEvent;

export type StepStatus = "running" | "done" | "error" | "denied" | "stopped";

export interface TextBlock {
  kind: "text";
  id: string;
  text: string;
  streaming: boolean;
}

export interface ReasoningBlock {
  kind: "reasoning";
  id: string;
  text: string;
  streaming: boolean;
}

export interface ToolBlock {
  kind: "tool";
  /** The action call id. */
  id: string;
  name: string;
  /** A `load-skill` action (Amy reading one of her playbooks). */
  skill?: boolean;
  input?: unknown;
  output?: unknown;
  error?: string;
  status: StepStatus;
  /** requestId of the approval this call is parked on, while unanswered. */
  awaitingApproval?: string;
}

export interface SubagentBlock {
  kind: "subagent";
  /** The parent's delegation call id. */
  id: string;
  name: string;
  /** What Amy asked the subagent to do (the delegation input). */
  task?: string;
  input?: unknown;
  status: StepStatus;
  /** The subagent's final report. */
  output?: string;
  error?: string;
  /** Its tool calls (inline subagents) and every question it asked. */
  children: Block[];
}

/**
 * - `open`: asked, the stream is still running (not answerable yet).
 * - `parked`: the session is waiting on it — answerable.
 * - `answered`: answered from this client (`response` holds the answer).
 * - `settled`: no longer pending, answer unknown (answered elsewhere, or the
 *   turn was stopped).
 */
export type QuestionStatus = "open" | "parked" | "answered" | "settled";

export interface QuestionBlock {
  kind: "question";
  /** The requestId. */
  id: string;
  request: InputRequest;
  /** Subagent that asked (undefined = Amy herself). */
  askedBy?: string;
  /** Tool approvals for a call shown on screen: the gated call's id. */
  toolCallId?: string;
  status: QuestionStatus;
  response?: InputResponse;
}

export interface AuthBlock {
  kind: "auth";
  id: string;
  name: string;
  description: string;
  challenge?: AuthorizationRequiredStreamEvent["data"]["authorization"];
  outcome?: AuthorizationOutcome;
}

export interface NoticeBlock {
  kind: "notice";
  id: string;
  tone: "stopped" | "error";
  text: string;
}

export type Block =
  | TextBlock
  | ReasoningBlock
  | ToolBlock
  | SubagentBlock
  | QuestionBlock
  | AuthBlock
  | NoticeBlock;

export interface UserItem {
  kind: "user";
  id: string;
  text: string;
  files: { filename?: string; mediaType: string }[];
}

export interface AssistantItem {
  kind: "assistant";
  id: string;
  blocks: Block[];
}

export type ChatItem = UserItem | AssistantItem;

export interface ThreadProjection {
  items: ChatItem[];
  /** Unanswered questions (open or parked), in ask order. */
  pending: string[];
  /** eve session generation (count of `session.started`) — scopes step keys
   * across a session rollover, where stream ordinals restart. */
  gen: number;
  /** Delegations currently running, innermost last. */
  openSubagents: { callId: string; name: string }[];
}

export const initialProjection = (): ThreadProjection => ({
  items: [],
  pending: [],
  gen: 0,
  openSubagents: [],
});

export function projectLog(log: readonly ThreadLogEvent[]): ThreadProjection {
  let state = initialProjection();
  for (const event of log) state = reduceThread(state, event);
  return state;
}

export function reduceThread(state: ThreadProjection, event: ThreadLogEvent): ThreadProjection {
  if (event.type === "client.input.responded") return answerQuestions(state, event.data.responses);
  const s = settleParkedQuestions(state, event);
  switch (event.type) {
    case "session.started":
      // A child session's start only ever arrives wrapped (subagent.event).
      return { ...s, gen: s.gen + 1, openSubagents: [] };
    case "message.received":
      return pushUser(s, event);
    case "step.started":
      return withCurrentAssistant(s, (item) => item);
    case "reasoning.appended":
    case "reasoning.completed":
    case "message.appended":
    case "message.completed":
      return upsertStreamBlock(s, event);
    case "actions.requested":
      return withCurrentAssistant(s, (item) => ({
        ...item,
        blocks: event.data.actions.reduce(addAction, item.blocks),
      }));
    case "action.result":
      return resolveAction(s, event);
    case "subagent.called":
      return openSubagent(s, event.data.callId, event.data.name);
    case "subagent.started":
      return openSubagent(s, event.data.callId, event.data.subagentName);
    case "subagent.event":
      return reduceSubagentChild(s, event.data.callId, event.data.event);
    case "subagent.completed":
      return closeSubagent(s, event.data.callId, (b) => ({
        ...b,
        status: b.status === "running" ? "done" : b.status,
        output: event.data.output || b.output,
      }));
    case "input.requested":
      return addQuestions(s, event.data.requests);
    case "authorization.required":
      return withCurrentAssistant(s, (item) => ({
        ...item,
        blocks: [
          ...item.blocks,
          {
            kind: "auth",
            id: `auth:${s.gen}:${event.data.sequence}:${event.data.name}`,
            name: event.data.name,
            description: event.data.description,
            challenge: event.data.authorization,
          },
        ],
      }));
    case "authorization.completed":
      return completeAuthorization(s, event.data.name, event.data.outcome);
    case "turn.cancelled":
      return endCurrentTurn(s, "stopped", "Stopped");
    case "turn.failed":
      return endCurrentTurn(s, "error", event.data.message || "Amy hit an error and stopped.");
    case "session.failed":
      return endCurrentTurn(s, "error", event.data.message || "Amy's session failed.");
    case "session.waiting":
      return sealOpenQuestions(s);
    default:
      return s;
  }
}

// ---------------------------------------------------------------------------
// Items

function pushUser(
  s: ThreadProjection,
  event: Extract<HandleMessageStreamEvent, { type: "message.received" }>
): ThreadProjection {
  const parts = event.data.parts;
  const text = parts
    ? parts.flatMap((p) => (p.type === "text" ? [p.text] : [])).join("\n")
    : event.data.message;
  const files = (parts ?? []).flatMap((p) =>
    p.type === "file" ? [{ filename: p.filename, mediaType: p.mediaType }] : []
  );
  return {
    ...s,
    items: [...s.items, { kind: "user", id: `u:${s.items.length}`, text, files }],
  };
}

/** Apply `fn` to the assistant item every non-user event lands in (created on demand). */
function withCurrentAssistant(
  s: ThreadProjection,
  fn: (item: AssistantItem) => AssistantItem
): ThreadProjection {
  const last = s.items[s.items.length - 1];
  if (last?.kind === "assistant") {
    const next = fn(last);
    if (next === last) return s;
    return { ...s, items: [...s.items.slice(0, -1), next] };
  }
  const created: AssistantItem = { kind: "assistant", id: `a:${s.items.length}`, blocks: [] };
  return { ...s, items: [...s.items, fn(created)] };
}

/** Rewrite the first block (searching newest items first, subagent children
 * included) that matches; returns the same state when nothing matched. */
function updateBlock(
  s: ThreadProjection,
  match: (b: Block) => boolean,
  fn: (b: Block) => Block
): ThreadProjection {
  for (let i = s.items.length - 1; i >= 0; i--) {
    const item = s.items[i]!;
    if (item.kind !== "assistant") continue;
    const blocks = rewriteBlocks(item.blocks, match, fn);
    if (blocks !== item.blocks) {
      const items = s.items.slice();
      items[i] = { ...item, blocks };
      return { ...s, items };
    }
  }
  return s;
}

function rewriteBlocks(
  blocks: Block[],
  match: (b: Block) => boolean,
  fn: (b: Block) => Block
): Block[] {
  for (let j = blocks.length - 1; j >= 0; j--) {
    const b = blocks[j]!;
    if (match(b)) {
      const next = blocks.slice();
      next[j] = fn(b);
      return next;
    }
    if (b.kind === "subagent") {
      const children = rewriteBlocks(b.children, match, fn);
      if (children !== b.children) {
        const next = blocks.slice();
        next[j] = { ...b, children };
        return next;
      }
    }
  }
  return blocks;
}

function findBlock(s: ThreadProjection, match: (b: Block) => boolean): Block | undefined {
  const walk = (blocks: Block[]): Block | undefined => {
    for (let j = blocks.length - 1; j >= 0; j--) {
      const b = blocks[j]!;
      if (match(b)) return b;
      if (b.kind === "subagent") {
        const hit = walk(b.children);
        if (hit) return hit;
      }
    }
    return undefined;
  };
  for (let i = s.items.length - 1; i >= 0; i--) {
    const item = s.items[i]!;
    if (item.kind !== "assistant") continue;
    const hit = walk(item.blocks);
    if (hit) return hit;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Text & reasoning

function upsertStreamBlock(
  s: ThreadProjection,
  event: Extract<
    HandleMessageStreamEvent,
    { type: "reasoning.appended" | "reasoning.completed" | "message.appended" | "message.completed" }
  >
): ThreadProjection {
  const isText = event.type.startsWith("message.");
  // `sequence` is the per-session turn ordinal — unlike `turnId`, it is set on
  // resumed turns too.
  const id = `${isText ? "t" : "r"}:${s.gen}:${event.data.sequence}:${event.data.stepIndex}`;
  let text: string | null;
  let streaming: boolean;
  switch (event.type) {
    case "message.appended":
      text = event.data.messageSoFar;
      streaming = true;
      break;
    case "message.completed":
      text = event.data.message;
      streaming = false;
      break;
    case "reasoning.appended":
      text = event.data.reasoningSoFar;
      streaming = true;
      break;
    case "reasoning.completed":
      text = event.data.reasoning;
      streaming = false;
      break;
  }
  return withCurrentAssistant(s, (item) => {
    const idx = item.blocks.findIndex((b) => b.id === id);
    // A step that completed without visible text drops its streamed draft.
    if (text === null) {
      return idx === -1 ? item : { ...item, blocks: item.blocks.filter((_, j) => j !== idx) };
    }
    const block: TextBlock | ReasoningBlock = isText
      ? { kind: "text", id, text, streaming }
      : { kind: "reasoning", id, text, streaming };
    if (idx === -1) return { ...item, blocks: [...item.blocks, block] };
    const blocks = item.blocks.slice();
    blocks[idx] = block;
    return { ...item, blocks };
  });
}

// ---------------------------------------------------------------------------
// Actions (tool calls, skills, delegations)

type ActionRequest = Extract<HandleMessageStreamEvent, { type: "actions.requested" }>["data"]["actions"][number];

const asString = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v : undefined;

/** The delegation brief — subagent inputs carry it under a few names. */
function taskOf(input: unknown): string | undefined {
  if (typeof input === "string") return asString(input);
  if (!input || typeof input !== "object") return undefined;
  const o = input as Record<string, unknown>;
  return asString(o.message) ?? asString(o.task) ?? asString(o.prompt) ?? asString(o.instructions);
}

function addAction(blocks: Block[], action: ActionRequest): Block[] {
  let block: Block | null;
  switch (action.kind) {
    case "subagent-call":
    case "remote-agent-call": {
      const name =
        action.kind === "subagent-call"
          ? action.subagentName || action.name
          : ((action as { remoteAgentName?: string }).remoteAgentName ?? action.name);
      block = {
        kind: "subagent",
        id: action.callId,
        name,
        task: taskOf(action.input),
        input: action.input,
        status: "running",
        children: [],
      };
      break;
    }
    case "tool-call": {
      const input = action.input as Record<string, unknown> | undefined;
      // The question card IS the ask_question call — no separate step line.
      block =
        action.toolName === "ask_question"
          ? null
          : action.toolName === "load_skill"
            ? {
                kind: "tool",
                id: action.callId,
                name: asString(input?.skill) ?? asString(input?.name) ?? "playbook",
                skill: true,
                input: action.input,
                status: "running",
              }
            : { kind: "tool", id: action.callId, name: action.toolName, input: action.input, status: "running" };
      break;
    }
    case "load-skill": {
      const input = action.input as Record<string, unknown> | undefined;
      block = {
        kind: "tool",
        id: action.callId,
        name: asString(input?.name) ?? asString(input?.skill) ?? "playbook",
        skill: true,
        input: action.input,
        status: "running",
      };
      break;
    }
    default:
      block = null;
  }
  if (!block) return blocks;
  // Calls can arrive incrementally — correlate by call id, never duplicate;
  // a re-sent request only refreshes the arguments.
  const idx = blocks.findIndex((b) => b.id === block.id);
  if (idx === -1) return [...blocks, block];
  const existing = blocks[idx]!;
  if (existing.kind !== "tool" && existing.kind !== "subagent") return blocks;
  const next = blocks.slice();
  next[idx] = { ...existing, input: action.input };
  return next;
}

function resolveAction(
  s: ThreadProjection,
  event: Extract<HandleMessageStreamEvent, { type: "action.result" }>
): ThreadProjection {
  const { result, status, error } = event.data;
  const denied = status === "rejected" || error?.code === "TOOL_EXECUTION_DENIED";
  if (result.kind === "subagent-result") {
    const failed = status === "failed" || result.isError === true;
    return closeSubagent(s, result.callId, (b) => ({
      ...b,
      status: denied ? "denied" : failed ? "error" : b.status === "running" ? "done" : b.status,
      output: b.output ?? asString(result.output),
      error: failed ? (error?.message ?? asString(result.output)) : undefined,
    }));
  }
  return updateBlock(
    s,
    (b) => b.kind === "tool" && b.id === result.callId,
    (b) => {
      const tool = b as ToolBlock;
      return {
        ...tool,
        awaitingApproval: undefined,
        status: denied ? "denied" : status === "failed" ? "error" : "done",
        output: "output" in result ? result.output : undefined,
        error: status === "failed" && !denied ? (error?.message ?? asString((result as { output?: unknown }).output)) : undefined,
      };
    }
  );
}

function openSubagent(s: ThreadProjection, callId: string, name: string): ThreadProjection {
  let next = s;
  if (!findBlock(s, (b) => b.kind === "subagent" && b.id === callId)) {
    next = withCurrentAssistant(s, (item) => ({
      ...item,
      blocks: [...item.blocks, { kind: "subagent", id: callId, name, status: "running", children: [] }],
    }));
  }
  if (next.openSubagents.some((o) => o.callId === callId)) return next;
  return { ...next, openSubagents: [...next.openSubagents, { callId, name }] };
}

function closeSubagent(
  s: ThreadProjection,
  callId: string,
  fn: (b: SubagentBlock) => SubagentBlock
): ThreadProjection {
  const next = updateBlock(s, (b) => b.kind === "subagent" && b.id === callId, (b) => fn(b as SubagentBlock));
  const open = next.openSubagents.filter((o) => o.callId !== callId);
  return open.length === next.openSubagents.length ? next : { ...next, openSubagents: open };
}

/** Inline subagents stream their own events wrapped in `subagent.event`:
 * surface their tool calls (and any question) under the delegation. */
function reduceSubagentChild(
  s: ThreadProjection,
  callId: string,
  child: HandleMessageStreamEvent
): ThreadProjection {
  const inSubagent = (fn: (children: Block[]) => Block[]) =>
    updateBlock(
      s,
      (b) => b.kind === "subagent" && b.id === callId,
      (b) => {
        const sub = b as SubagentBlock;
        const children = fn(sub.children);
        return children === sub.children ? sub : { ...sub, children };
      }
    );
  switch (child.type) {
    case "actions.requested":
      return inSubagent((children) => child.data.actions.reduce(addAction, children));
    case "action.result":
      return inSubagent((children) =>
        rewriteBlocks(
          children,
          (b) => b.kind === "tool" && b.id === child.data.result.callId,
          (b) => ({
            ...(b as ToolBlock),
            status: child.data.status === "failed" ? "error" : child.data.status === "rejected" ? "denied" : "done",
            output: "output" in child.data.result ? child.data.result.output : undefined,
            error: child.data.error?.message,
          })
        )
      );
    case "input.requested":
      return addQuestions(s, child.data.requests, callId);
    default:
      return s;
  }
}

// ---------------------------------------------------------------------------
// Questions & approvals

function addQuestions(
  s: ThreadProjection,
  requests: readonly InputRequest[],
  ownerCallId?: string
): ThreadProjection {
  let next = s;
  for (const request of requests) {
    const existing = findBlock(next, (b) => b.kind === "question" && b.id === request.requestId);
    if (existing) {
      // Re-emitted (replay / reconnect): refresh the payload, keep the state.
      next = updateBlock(next, (b) => b === existing, (b) => ({ ...(b as QuestionBlock), request }));
      continue;
    }
    // Amy's OWN tool approval — its call is on screen — is hers even while a
    // delegation runs in parallel; anything else asked mid-delegation came
    // from the subagent (eve proxies child questions onto Amy's stream).
    const ownTool = findBlock(
      next,
      (b) => b.kind === "tool" && b.id === request.action.callId
    );
    const owner = ownerCallId
      ? next.openSubagents.find((o) => o.callId === ownerCallId) ?? { callId: ownerCallId, name: "" }
      : ownTool
        ? undefined
        : next.openSubagents[next.openSubagents.length - 1];
    const question: QuestionBlock = {
      kind: "question",
      id: request.requestId,
      request,
      askedBy: owner?.name || undefined,
      toolCallId: ownTool?.id,
      status: "open",
    };
    if (ownTool) {
      next = updateBlock(next, (b) => b === ownTool, (b) => ({ ...(b as ToolBlock), awaitingApproval: request.requestId }));
    }
    const ownerBlock =
      owner && findBlock(next, (b) => b.kind === "subagent" && b.id === owner.callId);
    next = ownerBlock
      ? updateBlock(
          next,
          (b) => b === ownerBlock,
          (b) => ({ ...(b as SubagentBlock), children: [...(b as SubagentBlock).children, question] })
        )
      : withCurrentAssistant(next, (item) => ({ ...item, blocks: [...item.blocks, question] }));
    next = { ...next, pending: [...next.pending, request.requestId] };
  }
  return next;
}

function setQuestion(
  s: ThreadProjection,
  requestId: string,
  fn: (q: QuestionBlock) => QuestionBlock
): ThreadProjection {
  let next = updateBlock(s, (b) => b.kind === "question" && b.id === requestId, (b) => fn(b as QuestionBlock));
  const q = findBlock(next, (b) => b.kind === "question" && b.id === requestId) as QuestionBlock | undefined;
  if (q && (q.status === "answered" || q.status === "settled")) {
    next = updateBlock(
      next,
      (b) => b.kind === "tool" && b.awaitingApproval === requestId,
      (b) => ({ ...(b as ToolBlock), awaitingApproval: undefined })
    );
    if (next.pending.includes(requestId)) {
      next = { ...next, pending: next.pending.filter((id) => id !== requestId) };
    }
  }
  return next;
}

function answerQuestions(s: ThreadProjection, responses: readonly InputResponse[]): ThreadProjection {
  return responses.reduce(
    (acc, response) => setQuestion(acc, response.requestId, (q) => ({ ...q, status: "answered", response })),
    s
  );
}

const pendingQuestions = (s: ThreadProjection): QuestionBlock[] =>
  s.pending.flatMap((id) => {
    const q = findBlock(s, (b) => b.kind === "question" && b.id === id);
    return q ? [q as QuestionBlock] : [];
  });

/** `session.waiting` closes the stream: whatever is still open is now what
 * the session is parked on. */
function sealOpenQuestions(s: ThreadProjection): ThreadProjection {
  return pendingQuestions(s)
    .filter((q) => q.status === "open")
    .reduce((acc, q) => setQuestion(acc, q.id, (b) => ({ ...b, status: "parked" })), s);
}

/**
 * Answers ride the send request, so logs persisted before the client recorded
 * them (and answers given in another tab) have no record of the response.
 * A parked question is settled by WORK evidence only: the session cannot do
 * work while anything is still parked. Lifecycle events prove nothing —
 * answering one of several parallel approvals emits an empty bookkeeping
 * turn (turn.started → turn.completed → session.waiting) while the others
 * stay parked — and a request re-emitted by this very event must not settle
 * itself.
 */
function settleParkedQuestions(s: ThreadProjection, event: HandleMessageStreamEvent): ThreadProjection {
  if (s.pending.length === 0) return s;
  if (event.type.startsWith("session.") || event.type.startsWith("turn.")) return s;
  const fresh =
    event.type === "input.requested" ? new Set(event.data.requests.map((r) => r.requestId)) : undefined;
  return pendingQuestions(s)
    .filter((q) => q.status === "parked" && !fresh?.has(q.id))
    .reduce((acc, q) => setQuestion(acc, q.id, (b) => ({ ...b, status: "settled" })), s);
}

function completeAuthorization(
  s: ThreadProjection,
  name: string,
  outcome: AuthorizationOutcome
): ThreadProjection {
  return updateBlock(
    s,
    (b) => b.kind === "auth" && b.name === name && b.outcome === undefined,
    (b) => ({ ...(b as AuthBlock), outcome })
  );
}

// ---------------------------------------------------------------------------
// Stop / failure

/** A stopped or failed turn: close everything still running in the current
 * assistant item and leave a marker at its end. */
function endCurrentTurn(s: ThreadProjection, tone: "stopped" | "error", text: string): ThreadProjection {
  const ended: StepStatus = tone === "stopped" ? "stopped" : "error";
  const settleIds: string[] = [];
  const close = (blocks: Block[]): Block[] =>
    blocks.map((b) => {
      switch (b.kind) {
        case "text":
        case "reasoning":
          return b.streaming ? { ...b, streaming: false } : b;
        case "tool":
          return b.status === "running" ? { ...b, status: ended, awaitingApproval: undefined } : b;
        case "subagent":
          return {
            ...b,
            status: b.status === "running" ? ended : b.status,
            children: close(b.children),
          };
        case "question":
          if (b.status === "open" || b.status === "parked") {
            settleIds.push(b.id);
            return { ...b, status: "settled" };
          }
          return b;
        default:
          return b;
      }
    });
  const next = withCurrentAssistant(s, (item) => ({
    ...item,
    blocks: [
      ...close(item.blocks),
      { kind: "notice", id: `notice:${item.blocks.length}`, tone, text },
    ],
  }));
  return {
    ...next,
    openSubagents: [],
    pending: next.pending.filter((id) => !settleIds.includes(id)),
  };
}

// ---------------------------------------------------------------------------
// Selectors

/** Questions the user can answer right now (the session is parked on them). */
export function answerableQuestions(s: ThreadProjection): QuestionBlock[] {
  return pendingQuestions(s).filter((q) => q.status === "parked");
}

/** Mirrors eve's server-side `resolveTextToResponse` freeform rule. */
export const isFreeform = (request: InputRequest): boolean =>
  request.allowFreeform === true || !request.options?.length;

/** Plain-text recap of the visible conversation — handed to a fresh eve
 * session as context when the old one can no longer continue. */
export function transcriptRecap(s: ThreadProjection, maxChars = 8000): string {
  const lines: string[] = [];
  for (const item of s.items) {
    if (item.kind === "user") {
      lines.push(`User: ${item.text}`);
      continue;
    }
    const text = item.blocks
      .flatMap((b) => (b.kind === "text" ? [b.text] : b.kind === "subagent" && b.output ? [b.output] : []))
      .join("\n\n");
    if (text) lines.push(`Amy: ${text}`);
  }
  return lines.join("\n\n").slice(-maxChars);
}

// ---------------------------------------------------------------------------
// Persistence

/**
 * Drop superseded streaming snapshots before persisting: every
 * `*.appended` event carries the FULL text so far, so only the last one per
 * step matters, and none once the step's `*.completed` arrived (deltas were
 * ~80% of stored logs). Projecting the compacted log is identical.
 */
export function compactLog(log: readonly ThreadLogEvent[]): ThreadLogEvent[] {
  const keyOf = (e: ThreadLogEvent, gen: number): string | null => {
    switch (e.type) {
      case "message.appended":
      case "message.completed":
        return `m:${gen}:${e.data.sequence}:${e.data.stepIndex}`;
      case "reasoning.appended":
      case "reasoning.completed":
        return `r:${gen}:${e.data.sequence}:${e.data.stepIndex}`;
      default:
        return null;
    }
  };
  const last = new Map<string, number>();
  let gen = 0;
  log.forEach((e, i) => {
    if (e.type === "session.started") gen += 1;
    const key = keyOf(e, gen);
    if (key) last.set(key, i);
  });
  gen = 0;
  return log.filter((e, i) => {
    if (e.type === "session.started") gen += 1;
    if (e.type !== "message.appended" && e.type !== "reasoning.appended") return true;
    return last.get(keyOf(e, gen)!) === i;
  });
}
